/**
 * Human-readable display titles for the in-app AI agent's MCP tools, keyed by the
 * bare tool name (the Agent SDK's `mcp__<server>__<tool>` prefix is stripped
 * before lookup). This is the SINGLE SOURCE OF TRUTH for tool labels, consumed by:
 *
 *  - `packages/mcp-server` — sets each tool's `annotations.title` so external MCP
 *    clients (Claude Desktop, ChatGPT, Cursor, …) show a friendly name in
 *    `tools/list`.
 *  - `packages/web` — the agent chat "Used N tools" panel (`toolLabels.ts`).
 *
 * The Claude Agent SDK does not surface a tool's title on tool-call events, so the
 * web panel reads this map directly rather than receiving the title over the wire.
 *
 * Keep in sync with the tool registries: a missing entry for a registered
 * mcp-server tool is caught by that package's coverage test. Any tool NOT listed
 * here degrades gracefully — external clients fall back to the raw name, and the
 * web panel falls back to a prettified form of the name (never a raw `mcp__…`).
 *
 * The value type is intentionally `string | undefined` so every read is treated as
 * a lookup that can miss, keeping the fallback path honest across consumers.
 */
export const TOOL_TITLES: Partial<Record<string, string>> = {
  // Risks
  search_risks: 'Search risks',
  get_risk_by_id: 'Get risk details',
  get_risk_scores: 'Get risk scores',

  // Controls
  search_controls: 'Search controls',
  get_control_by_id: 'Get control details',

  // Actions
  search_actions: 'Search actions',
  get_action_by_id: 'Get action details',

  // Issues
  search_issues: 'Search issues',
  get_issue_by_id: 'Get issue details',

  // Obligations
  search_obligations: 'Search obligations',
  get_obligation_by_id: 'Get obligation details',

  // Third parties
  list_third_parties: 'List third parties',
  get_third_party_by_id: 'Get third-party details',

  // Enterprise risks
  list_enterprise_risks: 'List enterprise risks',
  get_enterprise_risk_by_id: 'Get enterprise-risk details',

  // Indicators (KRIs/KPIs)
  search_indicators: 'Search indicators',
  get_indicator_by_id: 'Get indicator details',
  list_indicator_results: 'List indicator results',

  // Documents (policies, standards, procedures)
  list_documents: 'List documents',
  get_document_by_id: 'Get document details',

  // Assessments
  list_assessments: 'List assessments',
  get_assessment_by_id: 'Get assessment details',

  // Cross-links
  get_linked_items: 'Get linked items',

  // Field schema (describe)
  get_entity_schema: 'Get register field schema',

  // Lookups
  list_tags: 'List tags',
  list_departments: 'List departments',

  // People
  list_users: 'List users',
  list_user_groups: 'List user groups',

  // Account (caller context)
  get_account: 'Get account details',

  // Platform help (in-process ai-agent tool, surfaced in our panel only)
  search_platform_docs: 'Search platform documentation',
};
